import { FIELD_LABELS, RESOURCE_LABELS, UNIT_LABELS, type UnitType } from '@bienenkampf/shared';
import { Badge, Button, Card, Progress } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useNow } from '../hooks/useNow.js';
import { FIELD_ICONS } from '../lib/hex.js';
import { formatAbsRel, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

const STATUS: Record<string, { label: string; tone: 'amber' | 'green' | 'sky' | 'neutral' }> = {
  FLYING: { label: 'Hinflug', tone: 'amber' },
  GATHERING: { label: 'Sammelt', tone: 'green' },
  STATIONED: { label: 'Stationiert', tone: 'green' },
  RETURNING: { label: 'Rückflug', tone: 'sky' },
  DONE: { label: 'Zurück', tone: 'neutral' },
};

export function MovesPage() {
  const { worldId: worldIdStr } = useParams({ from: '/w/$worldId' });
  const worldId = Number(worldIdStr);
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow();
  const moves = useQuery({
    ...trpc.movement.list.queryOptions({ worldId }),
    refetchInterval: 30_000,
  });
  const incoming = useQuery({
    ...trpc.movement.incoming.queryOptions({ worldId }),
    refetchInterval: 30_000,
  });
  const requestHelp = useMutation(
    trpc.alliance.createPlan.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.alliance.pathKey() }),
    }),
  );
  const cancel = useMutation(
    trpc.movement.cancel.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.movement.pathKey() }),
    }),
  );

  if (moves.isPending) return <p className="text-stone-400">Lade Züge…</p>;
  if (moves.isError) return <p className="text-red-400">{moves.error.message}</p>;
  const { active, done } = moves.data;

  const row = (m: (typeof active)[number], showCancel: boolean) => {
    const st = STATUS[m.status] ?? STATUS.DONE!;
    let phaseText = '';
    let progress: number | null = null;
    if (m.status === 'FLYING') {
      phaseText = `Ankunft ${formatAbsRel(m.arrivesAt, now)}`;
      progress =
        (now.getTime() - m.departedAt.getTime()) / (m.arrivesAt.getTime() - m.departedAt.getTime());
    } else if (m.status === 'GATHERING' && m.leavesAt) {
      phaseText = `sammelt bis ${formatAbsRel(m.leavesAt, now)}`;
      progress =
        (now.getTime() - m.arrivesAt.getTime()) / (m.leavesAt.getTime() - m.arrivesAt.getTime());
    } else if (m.status === 'RETURNING' && m.returnsAt) {
      phaseText = `zurück ${formatAbsRel(m.returnsAt, now)}`;
      const start = m.leavesAt
        ? Math.min(m.leavesAt.getTime(), m.returnsAt.getTime())
        : m.arrivesAt.getTime();
      progress = (now.getTime() - start) / (m.returnsAt.getTime() - start);
    } else if (m.returnsAt) {
      phaseText = `heimgekehrt ${formatAbsRel(m.returnsAt, now)}`;
    }
    const units = Object.entries(m.units)
      .filter(([, n]) => n > 0)
      .map(([t, n]) => `${formatNumber(n)} ${UNIT_LABELS[t as UnitType]}`)
      .join(', ');
    const cargo = m.cargo
      ? (['nectar', 'pollen', 'wax'] as const)
          .filter((r) => m.cargo![r] > 0)
          .map((r) => `${formatNumber(m.cargo![r])} ${RESOURCE_LABELS[r]}`)
          .join(', ')
      : '';
    return (
      <li key={m.id} className="rounded-lg bg-stone-900 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={st.tone}>{st.label}</Badge>
              <span className="font-medium">
                {m.toField
                  ? `${FIELD_ICONS[m.toField.type]} ${FIELD_LABELS[m.toField.type]} [${m.toField.q}|${m.toField.r}]`
                  : m.toHive
                    ? `${m.type === 'RAID' ? '⚔' : m.type === 'QUEEN_DELIVERY' ? '👑' : m.type === 'COLONIZE' ? '🏠' : m.type === 'SUPPORT' ? '🛡' : '🔭'} ${m.toHive.name} (${m.toHive.ownerName}) [${m.toHive.q}|${m.toHive.r}]`
                    : m.toHex
                      ? `🏠 Kolonie bei [${m.toHex.q}|${m.toHex.r}]`
                      : m.type}
              </span>
            </div>
            <div className="mt-1 text-xs text-stone-400">
              {m.type === 'QUEEN_DELIVERY' && 'Königinnen-Lieferung · '}
              {m.type === 'COLONIZE' && 'Kolonisation · '}
              {m.type === 'SUPPORT' && 'Unterstützung · '}
              {units || 'ohne Eskorte'} · von {m.fromHive.name}
            </div>
            <div className="text-xs text-stone-300">{phaseText}</div>
            {cargo && <div className="text-xs text-amber-300">Fracht: {cargo}</div>}
            {m.cargo && !cargo && m.status !== 'FLYING' && m.status !== 'GATHERING' && (
              <div className="text-xs text-stone-500">
                Fracht: nichts (Feld voll oder umgekehrt)
              </div>
            )}
          </div>
          {showCancel &&
            (m.status === 'FLYING' || m.status === 'GATHERING' || m.status === 'STATIONED') && (
              <Button
                variant="ghost"
                size="sm"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate({ worldId, movementId: m.id })}
                title={
                  m.status === 'FLYING'
                    ? 'Umkehren'
                    : m.status === 'STATIONED'
                      ? 'Unterstützung zurückrufen'
                      : 'Feld jetzt verlassen (Ertrag bis jetzt)'
                }
              >
                ↩
              </Button>
            )}
        </div>
        {progress !== null && (
          <Progress
            value={progress}
            className="mt-2"
            tone={m.status === 'RETURNING' ? 'sky' : 'amber'}
          />
        )}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {(incoming.data?.length ?? 0) > 0 && (
        <Card title={`⚠ Eingehend (${incoming.data!.length})`}>
          <ul className="flex flex-col gap-2">
            {incoming.data!.map((m) => (
              <li key={m.id} className="rounded-lg border border-red-900/60 bg-red-950/30 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={m.type === 'QUEEN_DELIVERY' ? 'green' : 'red'}>
                    {m.type === 'RAID'
                      ? '⚔ Raubzug'
                      : m.type === 'COLONIZE'
                        ? '🏠 Übernahme'
                        : '👑 Lieferung'}
                  </Badge>
                  <span className="font-medium">
                    {m.attackerName} → {m.targetName} [{m.targetQ}|{m.targetR}]
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-300">
                  <span>
                    Ankunft {formatAbsRel(m.arrivesAt, now)} · von {m.fromName} [{m.fromQ}|{m.fromR}
                    ]
                  </span>
                  {m.type !== 'QUEEN_DELIVERY' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={requestHelp.isPending}
                      onClick={() =>
                        requestHelp.mutate({
                          worldId,
                          kind: 'DEFENSE',
                          targetHiveId: m.targetId,
                          deadline: m.arrivesAt,
                          note: `${m.type === 'RAID' ? 'Raubzug' : 'Übernahme'} von ${m.attackerName}`,
                        })
                      }
                      title="Verteidigungsanfrage an den Stand"
                    >
                      🛡 Hilfe anfordern
                    </Button>
                  )}
                </div>
                {requestHelp.isSuccess && (
                  <div className="text-xs text-emerald-400">Anfrage an den Stand gesendet.</div>
                )}
                {requestHelp.error && (
                  <div className="text-xs text-red-400">{requestHelp.error.message}</div>
                )}
                <Progress
                  value={
                    (now.getTime() - m.departedAt.getTime()) /
                    (m.arrivesAt.getTime() - m.departedAt.getTime())
                  }
                  className="mt-2"
                  tone="amber"
                />
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title={`Unterwegs (${active.length})`}>
        {active.length === 0 && (
          <p className="text-sm text-stone-400">
            Keine Züge unterwegs. Schick Sammlerinnen von der Karte aus los.
          </p>
        )}
        <ul className="flex flex-col gap-2">{active.map((m) => row(m, true))}</ul>
        {cancel.error && <p className="mt-2 text-sm text-red-400">{cancel.error.message}</p>}
      </Card>
      {done.length > 0 && (
        <Card title="Zuletzt heimgekehrt">
          <ul className="flex flex-col gap-2">{done.map((m) => row(m, false))}</ul>
        </Card>
      )}
    </div>
  );
}
